import React, { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Button,
  Checkbox,
  DatePicker,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@roger/ui-components';
import { useStore } from '../../../store';
import type { Patient } from '../../../store/slices/patientsSlice';
import {
  getTakeOverCandidate,
  setEpisodeRogerChartsFrom,
  setPlannedRogerChartsFrom,
  type ExternalEmrMovePlan,
  type ExternalEmrPatientState,
} from '../../../services/externalEmrPatients';
import { getBusinessErrorMessage } from '../../../utils/apiErrorDetail';
import {
  formatDateForDisplay,
  formatDateToISO,
  formatDateToMMDDYYYY,
  parseISOToLocalDate,
} from '../../../utils/dateUtils';
import { addEpisodePath } from '../../../utils/navigation';
import TakeOverDialog from '../../episode/TakeOverDialog';
import {
  keptClaimsRogerCharts,
  keptClaimsWarning,
} from '../../episode/keptClaims';
import { keptClaimsStayLine } from '../../episode/takeOverCopy';
import { ImportedPatientBadge } from '../ImportedPatientBadge';
import { TestPatientBadge } from '../TestPatientBadge';
import { ExternalEmrImportControls } from './ExternalEmrImportControls';
import { ExternalEmrRendersLine } from './ExternalEmrRendersLine';
import {
  RECERT_CHARTS_FROM,
  CENSUS_COLUMN,
  CHARTS_FROM_CHANGE_ERROR,
  CHARTS_FROM_ESTIMATE_HELP,
  EDIT_CHARTS_FROM_LABEL,
  IMPORT_FIRST_LINE,
  MOVE_STATUS_HELP,
  MOVE_STATUS_LABEL,
  PATH_LABEL,
  PLANNED_CHIP,
  PLANNED_CHIP_HELP,
  plannedChartsFromSavedLine,
  PLAN_SAVE_ERROR,
  RECERT_HELP,
  TAKE_OVER_CONFIRM_HELP,
  TAKE_OVER_CONFIRM_LABEL,
  TAKE_OVER_NOT_OPEN,
  TAKE_OVER_OPEN_ERROR,
  USE_PROPOSAL_LABEL,
  chartsFromChangedLine,
  chartsFromPickerLabel,
  editChartsFromLabel,
  pathHelp,
  plannedNoLongerFitsLine,
  daysLeftLine,
} from './externalEmrPatientCopy';

export type ExternalEmrRowPatient = Patient & {
  external_emr: ExternalEmrPatientState;
};

/** The sheet's colors: moved green, overdue orange, due soon amber, slated unshaded. */
const ROW_TINT: Record<ExternalEmrMovePlan['status'], string> = {
  moved: 'bg-green-50',
  overdue: 'bg-orange-50',
  due_soon: 'bg-amber-50',
  slated: '',
};
const STATUS_TONE: Record<ExternalEmrMovePlan['status'], string> = {
  moved: 'border-green-300 bg-green-100 text-green-900',
  overdue: 'border-orange-300 bg-orange-100 text-orange-900',
  due_soon: 'border-amber-300 bg-amber-100 text-amber-900',
  slated: 'border-gray-300 bg-white text-gray-800',
};
const DAYS_LEFT_TONE: Record<ExternalEmrMovePlan['status'], string> = {
  moved: 'text-muted-foreground',
  overdue: 'font-medium text-orange-900',
  due_soon: 'font-medium text-amber-900',
  slated: 'text-muted-foreground',
};

/**
 * On every moved row, whenever the day Roger charts from falls inside a
 * claim the External EMR keeps: the dialog's warning, with the row's own
 * last line. A claim that had ended when the chart moved is left out, as
 * the dialog left it out. Warned, never blocked (Daniel, 2026-10-19).
 * Before the move no row warns: the estimate follows the planned day, as
 * Take Over's defaults do.
 */
const ChartsInsideKeptNote: React.FC<{
  day: string | null;
  keeps: ExternalEmrMovePlan['external_emr_keeps'];
}> = ({ day, keeps }) => {
  const inside = keptClaimsRogerCharts(keeps ?? [], day);
  const warning = day ? keptClaimsWarning(day, inside) : null;
  if (!warning) return null;
  return (
    <p
      className='w-full max-w-56 text-amber-900'
      data-testid='census-charts-inside-kept'
    >
      {warning} {keptClaimsStayLine(inside.length)}
    </p>
  );
};

interface ChartsFromCellProps {
  patient: ExternalEmrRowPatient;
  plan: ExternalEmrMovePlan;
  /** Render gate: `episode:take-over`. */
  canPlan: boolean;
  /** Take Over v2 is available to the viewer. */
  canTakeOver: boolean;
  /** Take Over is being opened for this row. */
  opening: boolean;
  /** Take Over is being opened for some row: every Confirm waits. */
  confirmBlocked: boolean;
  /** Open Take Over for this row's chart. */
  onConfirm: () => void;
  /** The ratified day changed: the row re-reads. */
  onChanged: () => void;
}

/**
 * Before the move: Roger's proposed charts-from day, or the day planned on
 * the census, in a picker for a planner. They may move it inside the
 * episode, no later than the day after the last day; clearing it returns to
 * the proposal. On an imported Take Over row, Confirm opens Take Over for
 * that day (Daniel, 2026-10-15: the date is set before the click).
 */
const PlannedChartsFromCell: React.FC<ChartsFromCellProps> = ({
  patient,
  plan,
  canPlan,
  canTakeOver,
  opening,
  confirmBlocked,
  onConfirm,
  onChanged,
}) => {
  const showError = useStore(state => state.showError);
  const showSuccess = useStore(state => state.showSuccess);
  const [planned, setPlanned] = useState(plan.roger_charts_from_planned);
  // A stored day Take Over would drop: the row shows the proposal instead.
  const [stale, setStale] = useState(plan.planned_no_longer_fits ?? false);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setPlanned(plan.roger_charts_from_planned);
    setStale(plan.planned_no_longer_fits ?? false);
  }, [plan.roger_charts_from_planned, plan.planned_no_longer_fits]);

  const census = patient.external_emr.census;
  const shown = planned ?? plan.roger_charts_from_proposed ?? null;
  const editable =
    canPlan &&
    !plan.roger_charts_from_locked &&
    !!census?.episode_start &&
    !!census?.episode_end &&
    (plan.path === 'take_over' || planned != null || stale);
  const confirmable = canPlan && canTakeOver && plan.path === 'take_over';

  const save = async (day: string | null) => {
    const before = planned;
    setPlanned(day);
    setSaving(true);
    try {
      const saved = await setPlannedRogerChartsFrom(patient.id, day);
      setPlanned(saved);
      setStale(false);
      if (saved) {
        showSuccess(plannedChartsFromSavedLine(formatDateForDisplay(saved)));
      }
      // The due day, the status and the chip counts follow the plan.
      onChanged();
    } catch (error) {
      console.error('Failed to save the planned charts-from day:', error);
      setPlanned(before);
      showError(getBusinessErrorMessage(error) ?? PLAN_SAVE_ERROR);
    } finally {
      setSaving(false);
    }
  };

  const staleNote = stale && (
    <p className='w-full text-muted-foreground' data-testid='census-plan-stale'>
      {plannedNoLongerFitsLine(
        formatDateToMMDDYYYY(plan.roger_charts_from_proposed ?? null) || null
      )}
    </p>
  );
  if (!shown && !editable && !stale) {
    return (
      <span className='text-muted-foreground' title={RECERT_CHARTS_FROM}>
        —
      </span>
    );
  }
  const chip = planned != null && (
    <span
      className='whitespace-nowrap rounded border border-blue-300 bg-blue-50 px-1.5 py-0.5 text-xs font-medium text-blue-900'
      title={PLANNED_CHIP_HELP}
      data-testid='census-planned-chip'
    >
      {PLANNED_CHIP}
    </span>
  );

  let day: React.ReactNode;
  if (!editable) {
    day = (
      <span data-testid='census-charts-from'>
        {formatDateToMMDDYYYY(shown) || '—'}
      </span>
    );
  } else {
    day = (
      <DatePicker
        className='w-36'
        datePickerSize='sm'
        aria-label={chartsFromPickerLabel(patient.name || patient.mrn || '')}
        date={parseISOToLocalDate(shown) ?? undefined}
        minDate={
          parseISOToLocalDate(plan.roger_charts_from_earliest ?? null) ??
          undefined
        }
        maxDate={
          parseISOToLocalDate(plan.roger_charts_from_latest ?? null) ??
          undefined
        }
        disabled={saving}
        allowClear={planned != null || stale}
        showClearButton={planned != null || stale}
        clearButtonLabel={USE_PROPOSAL_LABEL}
        onDateChange={date => {
          const next = date ? formatDateToISO(date) : null;
          if (next !== planned || stale) void save(next);
        }}
      />
    );
  }

  return (
    <div className='space-y-1'>
      <div className='flex flex-wrap items-center gap-1.5'>
        {day}
        {chip}
        {staleNote}
      </div>
      {confirmable &&
        (patient.external_emr.state === 'imported' ? (
          <Button
            variant='outline'
            size='sm'
            title={TAKE_OVER_CONFIRM_HELP}
            // Never open Take Over on a day the server has not stored.
            disabled={saving || opening || confirmBlocked}
            onClick={onConfirm}
            data-testid='census-take-over-confirm'
          >
            {opening && <Loader2 className='animate-spin' />}
            {TAKE_OVER_CONFIRM_LABEL}
          </Button>
        ) : (
          <p
            className='text-muted-foreground'
            data-testid='census-import-first'
          >
            {IMPORT_FIRST_LINE}
          </p>
        ))}
    </div>
  );
};

/**
 * After the move: the day Roger charts from, as the episode that moved the
 * chart holds it, as text. When Take Over moved it, a planner presses Edit
 * to change it inside that period (Daniel, 2026-10-15). Custody follows the
 * day; the toast counts the visits it handed over.
 */
const RatifiedChartsFromCell: React.FC<ChartsFromCellProps> = ({
  patient,
  plan,
  canPlan,
  canTakeOver,
  onChanged,
}) => {
  const showError = useStore(state => state.showError);
  const showSuccess = useStore(state => state.showSuccess);
  const [day, setDay] = useState(plan.roger_charts_from ?? null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    setDay(plan.roger_charts_from ?? null);
  }, [plan.roger_charts_from]);

  // Editing: focus the picker; a click outside it (and its calendar) or
  // Escape with no change returns to text.
  useEffect(() => {
    if (!editing) return;
    pickerRef.current?.querySelector('input')?.focus();
    const away = (event: MouseEvent) => {
      const target = event.target as Element | null;
      if (target && pickerRef.current?.contains(target)) return;
      if (target?.closest?.('[data-radix-popper-content-wrapper]')) return;
      setEditing(false);
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [editing]);

  if (!day) {
    return <span className='text-muted-foreground'>—</span>;
  }
  const episodeId = plan.roger_charts_from_episode_id ?? null;
  // The route needs Take Over v2 too: a basic Take Over also sets the
  // episode's taken-over stamp, so the episode alone would offer a 403.
  const editable =
    canPlan && canTakeOver && !!episodeId && !plan.roger_charts_from_locked;
  const rowName = patient.name || patient.mrn || '';

  const save = async (next: string) => {
    if (!episodeId) return;
    const before = day;
    setDay(next);
    setSaving(true);
    try {
      const result = await setEpisodeRogerChartsFrom(episodeId, next);
      setDay(result.roger_charts_from);
      showSuccess(
        chartsFromChangedLine(
          formatDateForDisplay(result.roger_charts_from),
          result.visits_now_roger ?? 0,
          result.visits_now_external ?? 0,
          result.visits_on_external_claims ?? 0,
          result.visits_on_roger_claims ?? 0
        )
      );
      onChanged();
    } catch (error) {
      console.error('Failed to change the day Roger charts from:', error);
      setDay(before);
      showError(getBusinessErrorMessage(error) ?? CHARTS_FROM_CHANGE_ERROR);
    } finally {
      setSaving(false);
    }
  };

  if (editable && editing) {
    return (
      <div
        ref={pickerRef}
        onKeyDown={event => {
          if (event.key === 'Escape') setEditing(false);
        }}
      >
        <DatePicker
          className='w-36'
          datePickerSize='sm'
          aria-label={chartsFromPickerLabel(rowName)}
          date={parseISOToLocalDate(day) ?? undefined}
          minDate={
            parseISOToLocalDate(plan.roger_charts_from_earliest ?? null) ??
            undefined
          }
          maxDate={
            parseISOToLocalDate(plan.roger_charts_from_latest ?? null) ??
            undefined
          }
          allowClear={false}
          onDateChange={date => {
            setEditing(false);
            const next = date ? formatDateToISO(date) : null;
            if (next && next !== day) void save(next);
          }}
        />
      </div>
    );
  }
  return (
    <div className='space-y-1'>
      <div className='flex flex-wrap items-center gap-1.5'>
        <span
          className={saving ? 'text-muted-foreground' : undefined}
          data-testid='census-charts-from'
        >
          {formatDateToMMDDYYYY(day) || '—'}
        </span>
        {editable && !saving && (
          <Button
            variant='link'
            size='sm'
            aria-label={editChartsFromLabel(rowName)}
            onClick={() => setEditing(true)}
            data-testid='census-charts-from-edit'
          >
            {EDIT_CHARTS_FROM_LABEL}
          </Button>
        )}
      </div>
      {/* After the move the claim is written as the External EMR's, and it
          may have billed it already: warn only, never move it. */}
      <ChartsInsideKeptNote day={day} keeps={plan.external_emr_keeps} />
    </div>
  );
};

/** The charts-from cell: a picker before the move, text with Edit after. */
const ChartsFromCell: React.FC<ChartsFromCellProps> = props =>
  props.plan.status === 'moved' ? (
    <RatifiedChartsFromCell {...props} />
  ) : (
    <PlannedChartsFromCell {...props} />
  );

interface ExternalEmrCensusTableProps {
  patients: ExternalEmrRowPatient[];
  /** Render gate for Import, Retry and Cancel (flag and `chart-import:start`). */
  canStart: boolean;
  /** Render gate for the charts-from day (`episode:take-over`). */
  canPlan: boolean;
  /** Take Over v2 is available to the viewer: gates Confirm on the row. */
  canTakeOver?: boolean;
  /** The agency cannot import now; the page header says why. */
  startBlocked?: boolean;
  selectable: boolean;
  selectedIds: Set<string>;
  onToggleSelected: (patient: Patient) => void;
  onOpen: (patient: Patient) => void;
  onChanged: () => void;
}

/**
 * The External EMR census as a worklist: one row per patient, ordered by the
 * server (overdue, then due soon and slated by due day, then moved). It
 * scrolls inside its own container on a phone.
 *
 * Confirm on a row reads the chart's Take Over candidate, as the chart page
 * does, and opens Take Over on it. Nothing moves until the person confirms
 * in the dialog: every chart is confirmed on its own.
 */
export const ExternalEmrCensusTable: React.FC<ExternalEmrCensusTableProps> = ({
  patients,
  canStart,
  canPlan,
  canTakeOver = false,
  startBlocked = false,
  selectable,
  selectedIds,
  onToggleSelected,
  onOpen,
  onChanged,
}) => {
  const showError = useStore(state => state.showError);
  const [opening, setOpening] = useState<string | null>(null);
  // The row whose candidate read is out. A read that lands after another
  // row took its place is dropped, so the dialog never opens the wrong chart.
  const openingRef = useRef<string | null>(null);
  const [takeOver, setTakeOver] = useState<{
    patientId: string;
    episodeId: string;
  } | null>(null);

  const openTakeOver = async (patientId: string) => {
    if (openingRef.current !== null) return;
    openingRef.current = patientId;
    setOpening(patientId);
    try {
      const candidate = await getTakeOverCandidate(patientId);
      if (openingRef.current !== patientId) return;
      if (candidate) {
        setTakeOver({ patientId, episodeId: candidate.episode_id });
      } else {
        showError(TAKE_OVER_NOT_OPEN);
      }
    } catch (error) {
      console.error('Failed to read the Take Over candidate:', error);
      if (openingRef.current === patientId) showError(TAKE_OVER_OPEN_ERROR);
    } finally {
      if (openingRef.current === patientId) {
        openingRef.current = null;
        setOpening(null);
      }
    }
  };

  return (
    <div className='w-full overflow-x-auto' data-testid='external-emr-census'>
      <Table className='min-w-[1150px] text-xs'>
        <TableHeader>
          <TableRow>
            {selectable && <TableHead className='w-8' />}
            <TableHead>{CENSUS_COLUMN.patient}</TableHead>
            <TableHead>{CENSUS_COLUMN.episodeStart}</TableHead>
            <TableHead>{CENSUS_COLUMN.episodeEnd}</TableHead>
            <TableHead title={RECERT_HELP}>{CENSUS_COLUMN.recert}</TableHead>
            <TableHead>{CENSUS_COLUMN.path}</TableHead>
            <TableHead title={CHARTS_FROM_ESTIMATE_HELP}>
              {CENSUS_COLUMN.chartsFrom}
            </TableHead>
            <TableHead>{CENSUS_COLUMN.moveBy}</TableHead>
            <TableHead>{CENSUS_COLUMN.status}</TableHead>
            <TableHead>{CENSUS_COLUMN.actions}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {patients.map(patient => {
            const external = patient.external_emr;
            const plan = external.move_plan ?? null;
            const census = external.census;
            const name = patient.name || 'Unknown Patient';
            const seedAt = patient.listed_at ?? null;
            return (
              <TableRow
                key={patient.id}
                className={plan ? ROW_TINT[plan.status] : ''}
                data-testid='external-emr-census-row'
                data-status={plan?.status ?? ''}
              >
                {selectable && (
                  <TableCell>
                    <Checkbox
                      checked={selectedIds.has(patient.id)}
                      onCheckedChange={() => onToggleSelected(patient)}
                      aria-label={`Select ${name}`}
                      data-testid='external-emr-select'
                    />
                  </TableCell>
                )}
                <TableCell>
                  <div className='flex flex-wrap items-center gap-1.5'>
                    <button
                      type='button'
                      className='text-left font-medium text-primary underline-offset-2 hover:underline'
                      onClick={() => onOpen(patient)}
                    >
                      {name}
                    </button>
                    {patient.is_test_patient && (
                      <TestPatientBadge className='shrink-0' />
                    )}
                    {external.state === 'imported' &&
                      !patient.import_badge_hidden_at && (
                        <ImportedPatientBadge
                          importedAt={patient.imported_at}
                          className='shrink-0'
                        />
                      )}
                  </div>
                  <div className='text-muted-foreground'>
                    MRN: {patient.mrn || 'N/A'}
                  </div>
                </TableCell>
                <TableCell className='whitespace-nowrap'>
                  {formatDateToMMDDYYYY(census?.episode_start ?? null) || '—'}
                </TableCell>
                <TableCell className='whitespace-nowrap'>
                  {formatDateToMMDDYYYY(census?.episode_end ?? null) || '—'}
                </TableCell>
                <TableCell className='whitespace-nowrap' title={RECERT_HELP}>
                  {formatDateToMMDDYYYY(census?.episode_end ?? null) || '—'}
                </TableCell>
                <TableCell className='whitespace-nowrap'>
                  {plan ? (
                    <span
                      title={pathHelp(
                        plan.path,
                        plan.path === 'take_over' ? plan.move_by : null
                      )}
                    >
                      {PATH_LABEL[plan.path]}
                    </span>
                  ) : (
                    '—'
                  )}
                </TableCell>
                <TableCell>
                  {plan ? (
                    <ChartsFromCell
                      patient={patient}
                      plan={plan}
                      canPlan={canPlan}
                      canTakeOver={canTakeOver}
                      opening={opening === patient.id}
                      confirmBlocked={opening !== null}
                      onConfirm={() => void openTakeOver(patient.id)}
                      onChanged={onChanged}
                    />
                  ) : (
                    '—'
                  )}
                </TableCell>
                <TableCell className='whitespace-nowrap'>
                  {plan ? (
                    <>
                      <div data-testid='census-due-on'>
                        {formatDateToMMDDYYYY(plan.due_on)}
                      </div>
                      {plan.status !== 'moved' && (
                        <div className={DAYS_LEFT_TONE[plan.status]}>
                          {daysLeftLine(plan.days_left)}
                        </div>
                      )}
                    </>
                  ) : (
                    '—'
                  )}
                </TableCell>
                <TableCell>
                  {plan && (
                    <span
                      className={`whitespace-nowrap rounded border px-1.5 py-0.5 font-medium ${STATUS_TONE[plan.status]}`}
                      title={MOVE_STATUS_HELP[plan.status]}
                      data-testid='census-move-status'
                    >
                      {MOVE_STATUS_LABEL[plan.status]}
                    </span>
                  )}
                </TableCell>
                <TableCell className='min-w-[14rem]'>
                  <div className='space-y-1'>
                    <ExternalEmrRendersLine
                      patientId={patient.id}
                      external={external}
                      seedAt={seedAt}
                      className='text-xs'
                      canRefresh={canStart && !startBlocked}
                    />
                    <ExternalEmrImportControls
                      patientId={patient.id}
                      external={external}
                      canStart={canStart}
                      startBlocked={startBlocked}
                      seedAt={seedAt}
                      onChanged={onChanged}
                      // The name cell already says Imported.
                      showImportedPill={false}
                      recertHref={
                        external.state === 'imported' && external.ready
                          ? addEpisodePath(patient.id)
                          : null
                      }
                    />
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {takeOver && (
        <TakeOverDialog
          key={takeOver.patientId}
          open
          episodeId={takeOver.episodeId}
          onClose={() => setTakeOver(null)}
          onTakenOver={() => {
            setTakeOver(null);
            onChanged();
          }}
        />
      )}
    </div>
  );
};
